// frontend/src/native/iap.ts — Native In-App-Käufe (cordova-plugin-purchase)
//
// Phase 5: direkte Store-Käufe gegen Apple/Google; nach erfolgreichem Kauf
// wird das rohe Receipt (iOS) bzw. der purchaseToken (Android) an die
// bestehenden Server-Endpoints geschickt, die die Coins gutschreiben.
//
// Auf Web/PWA ist alles ein No-Op (purchaseCoins wirft IapError('unavailable')).
//
// Hinweis: cordova-plugin-purchase wird per Side-effect-Import geladen
// (das Plugin clobbert die globale Variable `CdvPurchase`). Der dynamische
// Import sorgt dafür, dass Web-Bundles das Plugin nicht anziehen. Die
// echten Typen leben in `vite-env.d.ts` (Ambient-DTS via package.json
// `types`-Feld), sodass hier nur der globale `CdvPurchase`-Namespace
// verfuegbar ist.

import { isNativeCapacitorPlatform } from './bridge';
import { runningEnv } from '../env/appEnv';
import {
  verifyAppleReceipt,
  verifyGooglePurchase,
  refreshAppleSubs,
  refreshGoogleSubs,
  getCoinIapPackages, getVerificationStatus, getGoogleReplaceCandidate,
  type VerificationResponse,
} from '../pages/coins/api';
import { getMembership } from '../api/membership';
// App-weiter QueryClient wird erst bei Bedarf geladen: ein statischer Import
// zöge `new QueryClient()` in jeden Modulgraphen, der iap.ts berührt (Tests mit
// react-query-Teilmocks brechen sonst).
async function getAppQueryClient(): Promise<Pick<QueryClient, 'invalidateQueries'>> {
  return (await import('../app/queryClient')).queryClient;
}
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { nativeRequestInfo } from './requestInfo';
import { trackFunnel, type FunnelEventData } from '../api/funnel';
import { IapError, fromStoreError, fromVerificationError, classifyPurchaseFailure, createAttemptId, createPurchaseAttempt,
  setPurchasePhase, purchaseDiagnostics, type PurchaseAttempt } from './purchaseDiagnostics';
export { IapError, fromStoreError, fromVerificationError, classifyPurchaseFailure, createPurchaseAttempt, purchaseDiagnostics } from './purchaseDiagnostics';
export type { IapErrorCode } from './purchaseDiagnostics';
import type { CoinBalance } from '../api/types';

// ─── Öffentliche Typen ──────────────────────────────────────────────────────

export interface IapProduct {
  productId: string;
  coins: number;
  priceEur: number;
  name: string;
}

export interface IapPurchaseResult {
  ok: true;
  coins: number;
  status?: 'pending' | 'verified';
  balance?: number;
}

export interface IapStorePrice {
  formatted: string;
  currency: string | null;
}

// ─── Abo-Produkte (Flatrates) ────────────────────────────────────────────────
// Stabiles Mapping tier+duration ↔ Store-Produkt. Apple hat 8 separate
// productIds; Google hat EIN Produkt (`eu.fluesterfeder.app.flat`) mit 8 base
// plans. Die Preise dienen NUR als Fallback-Anzeige; der echte Preis kommt aus
// dem StoreKit-/Play-Offer bzw. der Server-Preisliste.
export type FlatTier = 'budget' | 'normal' | 'premium' | 'legendary';
export type FlatDuration = 'weekly' | 'monthly';
export type MembershipTier = 'plus' | 'ultra' | 'founders';
export type MembershipDuration = 'week' | 'month' | 'year';
type SubscriptionTier = FlatTier | MembershipTier;
type SubscriptionDuration = FlatDuration | MembershipDuration;

export interface IapSubscriptionProduct {
  tier: SubscriptionTier;
  duration: SubscriptionDuration;
  appleProductId: string;
  googleProductId?: string;
  googleBasePlanId: string;
  priceEur: number;
}

// App Review 2.3.10: Der Google-Produkt-Identifier wird zur Laufzeit
// zusammengesetzt, damit er nicht als Literal im iOS-Binary landet (analog zu
// ANDROID_CDV_PLATFORM weiter unten). Laufzeitwert = 'eu.fluesterfeder.app.flat'.
const GOOGLE_SUB_PRODUCT_ID = ['eu.fluesterfeder.app', 'flat'].join('.');
// Paket 75-kauf/4: aus GOOGLE_SUB_PRODUCT_ID abgeleitet statt eines eigenen
// Bundle-ID-Literals (siehe Kommentar oben) — Laufzeitwert
// 'eu.fluesterfeder.app', identisch zu server/apple.js APPLE_BUNDLE_ID.
const APP_BUNDLE_ID = GOOGLE_SUB_PRODUCT_ID.replace(/\.flat$/, '');

export const SUBSCRIPTION_PRODUCTS: readonly IapSubscriptionProduct[] = [
  { tier: 'budget',    duration: 'weekly',  appleProductId: 'eu.fluesterfeder.app.flat_budget_weekly',     googleBasePlanId: 'budget-weekly',     priceEur:   5.99 },
  { tier: 'budget',    duration: 'monthly', appleProductId: 'eu.fluesterfeder.app.flat_budget_monthly',    googleBasePlanId: 'budget-monthly',    priceEur:  19.99 },
  { tier: 'normal',    duration: 'weekly',  appleProductId: 'eu.fluesterfeder.app.flat_normal_weekly',     googleBasePlanId: 'normal-weekly',     priceEur:  11.99 },
  { tier: 'normal',    duration: 'monthly', appleProductId: 'eu.fluesterfeder.app.flat_normal_monthly',    googleBasePlanId: 'normal-monthly',    priceEur:  39.99 },
  { tier: 'premium',   duration: 'weekly',  appleProductId: 'eu.fluesterfeder.app.flat_premium_weekly',    googleBasePlanId: 'premium-weekly',    priceEur:  44.99 },
  { tier: 'premium',   duration: 'monthly', appleProductId: 'eu.fluesterfeder.app.flat_premium_monthly',   googleBasePlanId: 'premium-monthly',   priceEur: 149.99 },
  { tier: 'legendary', duration: 'weekly',  appleProductId: 'eu.fluesterfeder.app.flat_legendary_weekly',  googleBasePlanId: 'legendary-weekly',  priceEur:  74.99 },
  { tier: 'legendary', duration: 'monthly', appleProductId: 'eu.fluesterfeder.app.flat_legendary_monthly', googleBasePlanId: 'legendary-monthly', priceEur: 249.99 },
  { tier: 'plus', duration: 'week', appleProductId: 'eu.fluesterfeder.app.plus_week', googleProductId: 'lorath_plus', googleBasePlanId: 'week', priceEur: 2.99 },
  { tier: 'plus', duration: 'month', appleProductId: 'eu.fluesterfeder.app.plus_month', googleProductId: 'lorath_plus', googleBasePlanId: 'month', priceEur: 7.99 },
  { tier: 'plus', duration: 'year', appleProductId: 'eu.fluesterfeder.app.plus_year', googleProductId: 'lorath_plus', googleBasePlanId: 'year', priceEur: 79.99 },
  { tier: 'ultra', duration: 'week', appleProductId: 'eu.fluesterfeder.app.ultra_week', googleProductId: 'lorath_ultra', googleBasePlanId: 'week', priceEur: 6.99 },
  { tier: 'ultra', duration: 'month', appleProductId: 'eu.fluesterfeder.app.ultra_month', googleProductId: 'lorath_ultra', googleBasePlanId: 'month', priceEur: 19.99 },
  { tier: 'ultra', duration: 'year', appleProductId: 'eu.fluesterfeder.app.ultra_year', googleProductId: 'lorath_ultra', googleBasePlanId: 'year', priceEur: 199.99 },
  { tier: 'founders', duration: 'week', appleProductId: 'eu.fluesterfeder.app.founders_week', googleProductId: 'lorath_founders', googleBasePlanId: 'week', priceEur: 14.99 },
  { tier: 'founders', duration: 'month', appleProductId: 'eu.fluesterfeder.app.founders_month', googleProductId: 'lorath_founders', googleBasePlanId: 'month', priceEur: 39.99 },
  { tier: 'founders', duration: 'year', appleProductId: 'eu.fluesterfeder.app.founders_year', googleProductId: 'lorath_founders', googleBasePlanId: 'year', priceEur: 399.99 },
];

const APPLE_SUB_IDS = new Set<string>(SUBSCRIPTION_PRODUCTS.map((s) => s.appleProductId));
const GOOGLE_SUB_IDS = new Set<string>([
  GOOGLE_SUB_PRODUCT_ID,
  ...SUBSCRIPTION_PRODUCTS.flatMap((s) => s.googleProductId ? [s.googleProductId] : []),
]);

function findSubByTierDuration(tier: SubscriptionTier, duration: SubscriptionDuration): IapSubscriptionProduct | undefined {
  return SUBSCRIPTION_PRODUCTS.find((s) => s.tier === tier && s.duration === duration);
}

// Modul-State für den aktuell laufenden Google-Abo-Kauf: Google liefert für ALLE
// base plans dieselbe productId (`flat`), daher merken wir uns den base plan der
// aktuell laufenden Transaktion, damit handleApproved ihn an den Server-Verify
// weiterreichen kann. Subscriptions werden in der UI seriell gekauft.
let googleSubBasePlanInFlight: string | null = null;
let preparingSubscription = false;
let restoringSubscriptions = false;

export function isSubscriptionProductId(productId: string): boolean {
  return APPLE_SUB_IDS.has(productId) || GOOGLE_SUB_IDS.has(productId);
}

export interface SubscriptionPurchaseConfig {
  attempt?: PurchaseAttempt;
  funnel?: FunnelEventData;
  googleAccountId?: string;
  resolvedOffer?: ResolvedPurchaseOffer;
  appleProductId?: string;
  googleProductId?: string;
  googleBasePlanId?: string;
  googleOfferId?: string;
  appleDiscount?: CdvPurchase.AppleAppStore.PaymentDiscount;
  // P3: Upgrade/Downgrade über den Store.
  //   - Android: zusätzlich `oldPurchaseToken` (aus localReceipts) +
  //     `ReplacementMode` an `store.order()` durchreichen.
  //   - iOS: kein zusätzliches Feld nötig — Apple verrechnet innerhalb der
  //     Abo-Gruppe selbst. Das replace-Flag landet im Diagnose-Log, damit
  //     „warum wurde der Tarif trotzdem gewechselt"-Bugs nachvollziehbar sind.
  replace?: { mode: 'upgrade' | 'downgrade'; oldPurchaseToken?: string | null; oldProductId?: string | null;
    oldBasePlanId?: string | null; contractRef?: string | null; contractHasAccountId?: boolean };
}

/**
 * R6-02-c: Kaufsperre im Beta-Modus. Die Beta-Umgebung hat auf dem Server
 * VOLLSTAENDIGE Apple-, Google-, PayPal- und Stripe-Zugangsdaten — ein Kauf dort
 * wird echt abgebucht und auf einem Konto der BETA-Datenbank gutgeschrieben.
 * Die Sperre sitzt bewusst tief im Modul (nicht nur in der Oberflaeche), damit
 * kein Aufrufer sie versehentlich umgeht.
 */
export function purchasesBlockedByEnvironment(): boolean {
  return runningEnv() === 'beta';
}

// ─── Modul-State ────────────────────────────────────────────────────────────

interface PendingEntry {
  resolve: (result: IapPurchaseResult) => void;
  reject: (error: IapError) => void;
  timeoutId: number;
  attempt?: PurchaseAttempt;
  platform?: 'ios' | 'android';
  approved?: boolean;
  recovery?: boolean;
  existingTransactions?: Set<string>;
  funnel?: FunnelEventData;
}

let detailsLoadedAt = 0;
let handlersRegistered = false;
let storeInitializationCalled = false;
let initialized = false;
let initPromise: Promise<void> | null = null;
let activePlatform: 'ios' | 'android' | null = null;
// DIAGNOSE (7.1.0.65): die zuletzt an initIap übergebene Produktliste (für einen
// On-Demand-Reinit beim Kauf-Tap) + der genaue Grund, WARUM die Init zuletzt
// nicht durchlief. Wird in die Fehlermeldung gespiegelt, damit ein „nicht
// verfügbar" auf dem Release-Build (ohne WebView-Debugging) diagnostizierbar ist.
let lastProducts: IapProduct[] = [];
let lastIapInitError: string | null = null;
// Letzter vom Store gemeldeter Fehler (auch OHNE laufenden Kauf, z.B. ein
// Produkt-Lade-Fehler ERR_LOAD von Google direkt nach initialize).
let lastStoreError: string | null = null;
// DIAGNOSE (7.1.0.67): Produktzahlen im Init-Verlauf — wie viele wurden registriert
// (inN), wie viele kennt der Store direkt NACH register (regN) und NACH initialize
// (postN). Klärt bei prods=0, ob register nicht greift oder initialize sie verwirft.
let lastInitDetail = 'n/a';

function storeProductCount(store: unknown): number | string {
  const arr = (store as { products?: unknown[] })?.products;
  return Array.isArray(arr) ? arr.length : '?';
}

// Kompakter Diagnose-String: Init-Status, Store-Präsenz, Plattform, letzter Init-
// und Store-Fehler + (optional) was der Store für ein konkretes Produkt geladen hat
// (Gesamtzahl geladener Produkte, ob das getippte gefunden wurde, Offer-Anzahl).
// Landet im Fehler-Toast, damit der Grund auf dem Release-Build ablesbar ist.
export function getIapDiag(productId?: string): string {
  const cdv = getCdv();
  const store = cdv?.store;
  let extra = '';
  try {
    const prods = store && Array.isArray((store as { products?: unknown[] }).products)
      ? (store as { products: unknown[] }).products.length : '?';
    extra = ` prods=${prods}`;
    if (store && productId) {
      const plat = getCapacitorPlatform() === 'ios'
        ? cdv.Platform.APPLE_APPSTORE
        : (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform);
      const p = store.get(productId, plat);
      extra += ` found=${!!p} offers=${p?.offers?.length ?? 0}`;
    }
  } catch {
    extra += ' diagErr';
  }
  return `init=${initialized} cdv=${!!cdv} store=${!!store} plat=${getCapacitorPlatform() ?? 'null'} err=${lastIapInitError ?? 'none'} sErr=${lastStoreError ?? 'none'} [${lastInitDetail}]${extra}`;
}
// FIX 03-P2-11: begrenzte Auto-Retry-Zählung für die IAP-Init (One-Shot vorher →
// bei langsamem Boot / verzögert injiziertem Cordova-Plugin blieb der Shop die ganze
// Session leer, ohne Neuversuch).
let iapInitAttempts = 0;
const IAP_INIT_MAX_ATTEMPTS = 3;
// key = productId. Eine pending-Purchase pro Produkt-ID, weil der Store pro
// Kauf immer genau eine Transaktion liefert; bei mehreren parallelen Käufen
// desselben Produkts wäre der zweite ohnehin im Store-UI serialisiert.
const pending = new Map<string, PendingEntry>();

// Sicherheits-Timeout: wenn weder approved noch error feuert (z.B. wenn das
// native Sheet hängt), wird die pending-Promise sauber rejected.
const PURCHASE_TIMEOUT_MS = 180_000;

// ─── Helpers ────────────────────────────────────────────────────────────────

function getCapacitorPlatform(): 'ios' | 'android' | null {
  if (typeof window === 'undefined') return null;
  const cap = (window as Window & {
    Capacitor?: { getPlatform?: () => string };
  }).Capacitor;
  const raw = cap?.getPlatform?.();
  if (raw === 'ios') return 'ios';
  if (raw === 'android') return 'android';
  return null;
}

function getCdv(): typeof CdvPurchase | null {
  if (typeof window === 'undefined') return null;
  const win = window as Window & { CdvPurchase?: typeof CdvPurchase };
  return win.CdvPurchase ?? null;
}

// Wartet, bis der Cordova-Bridge `window.CdvPurchase` injiziert hat. Pollt in
// festen Intervallen (kein Date.now noetig) bis maxTries erreicht ist.
async function waitForCdvPurchase(maxTries = 100, intervalMs = 100): Promise<typeof CdvPurchase | null> {
  let cdv = getCdv();
  for (let i = 0; i < maxTries && !cdv; i += 1) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, intervalMs));
    cdv = getCdv();
  }
  return cdv;
}

function clearPending(productId: string): void {
  const entry = pending.get(productId);
  if (!entry) return;
  pending.delete(productId);
  if (GOOGLE_SUB_IDS.has(productId)) googleSubBasePlanInFlight = null;
  if (typeof window !== 'undefined' && entry.timeoutId) {
    window.clearTimeout(entry.timeoutId);
  }
}

function rejectPending(productId: string, error: IapError): void {
  const entry = pending.get(productId);
  if (!entry) return;
  clearPending(productId);
  entry.reject(error);
}

function resolvePending(productId: string, result: IapPurchaseResult): void {
  const entry = pending.get(productId);
  if (!entry) return;
  clearPending(productId);
  entry.resolve(result);
}

function getProductCoinAmount(products: IapProduct[], productId: string): number {
  const match = products.find((p) => p.productId === productId);
  return match?.coins ?? 0;
}

// Wartet, bis der Store das Default-Offer eines Produkts geladen hat. Offers
// laden NACH store.initialize() asynchron nach; ein sehr schneller Kauf-Tap kann
// sonst auf ein noch nicht geladenes Offer treffen → IapError('offer_not_loaded')
// (= Julius' „in dieser App-Version nicht verfuegbar"-Fehler). Pollt bis maxMs.
// Wählt das für den User GÜNSTIGSTE verfügbare Offer eines (Einmal-)Produkts.
// Hintergrund Funke-Einmalrabatt: Google liefert das Rabatt-Offer NUR an User,
// die dafür berechtigt sind (redemptionLimit=1, once-per-user). Wir nehmen daher
// das billigste Offer → Rabatt für Berechtigte, Basispreis für alle anderen
// (bereits eingelöst → Google liefert das Rabatt-Offer nicht mehr). Pakete ohne
// Rabatt haben nur EIN Offer → unverändert getOffer(). Fallback: getOffer().
function pickBestOffer(product: CdvPurchase.Product | undefined): CdvPurchase.Offer | undefined {
  if (!product) return undefined;
  const offers = product.offers ?? [];
  if (offers.length <= 1) return product.getOffer();
  let best: CdvPurchase.Offer | undefined;
  let bestMicros = Number.POSITIVE_INFINITY;
  for (const o of offers) {
    const phases = o.pricingPhases ?? [];
    const phase = phases[phases.length - 1] ?? phases[0];
    const micros = Number((phase as { priceMicros?: number | string } | undefined)?.priceMicros);
    if (Number.isFinite(micros) && micros >= 0 && micros < bestMicros) {
      bestMicros = micros;
      best = o;
    }
  }
  return best ?? product.getOffer();
}

// Liefert den vom Store bereits lokalisierten Preis eines Coin-Pakets. Der
// String wird in der UI unveraendert angezeigt; currency dient nur dem Hinweis,
// welche Waehrung die sichtbaren Store-Preise verwenden.
export function getCoinStorePrice(productId: string): IapStorePrice | null {
  if (!initialized) return null;
  const cdvPlatform = getCapacitorPlatform();
  if (!cdvPlatform) return null;
  const cdv = getCdv();
  const store = cdv?.store;
  const Platform = cdv?.Platform;
  if (!store || !Platform) return null;
  const platformEnum: CdvPurchase.Platform = cdvPlatform === 'android'
    ? (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform)
    : Platform.APPLE_APPSTORE;
  const offer = pickBestOffer(store.get(productId, platformEnum));
  const phases = offer?.pricingPhases ?? [];
  const phase = phases[phases.length - 1] ?? phases[0];
  if (!phase?.price) return null;
  return {
    formatted: phase.price,
    currency: typeof phase.currency === 'string' && phase.currency ? phase.currency : null,
  };
}

// FIX-Versuch (7.1.0.66): erzwingt eine frische Produkt-/Offer-Abfrage beim Store
// (Google/Apple), falls die Offers nach initialize nicht (rechtzeitig) geladen
// wurden — genau Julius' „offer_not_loaded". store.update() ist best-effort +
// idempotent; Fehler werden ignoriert.
function triggerStoreRefresh(store: CdvPurchase.Store): void {
  try {
    const s = store as unknown as { update?: () => unknown };
    if (typeof s.update === 'function') void Promise.resolve(s.update()).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

async function waitForOffer(
  store: CdvPurchase.Store,
  productId: string,
  platformEnum: CdvPurchase.Platform,
  maxMs = 10000,
  intervalMs = 150,
): Promise<CdvPurchase.Offer | undefined> {
  let offer = pickBestOffer(store.get(productId, platformEnum));
  // Kein Offer sofort da → einmal einen Store-Refresh anstoßen und dann pollen.
  if (!offer) triggerStoreRefresh(store);
  const maxTries = Math.max(1, Math.ceil(maxMs / intervalMs));
  for (let i = 0; i < maxTries && !offer; i += 1) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, intervalMs));
    offer = pickBestOffer(store.get(productId, platformEnum));
  }
  return offer ?? undefined;
}

export interface ResolvedPurchaseOffer {
  kind: 'trial' | 'discount' | 'regular';
  todayAmount: string;
  firstChargeAt: string | null;
  recurringPrice: string;
  offerId: string;
}

interface PurchaseOfferOptions {
  /** Must include the server catalog's trial_available and the plan relation. */
  wantTrial: boolean;
  wantDiscount: boolean;
  appleProductId?: string;
  googleProductId?: string;
  googleBasePlanId?: string;
}

// Keep the exact store object and a price snapshot; never silently reselect at checkout.
const resolvedOfferBindings = new WeakMap<ResolvedPurchaseOffer, {
  offer: CdvPurchase.Offer; productId: string; phases: string; tier: SubscriptionTier; duration: SubscriptionDuration;
}>();

function isFreePhase(phase: CdvPurchase.PricingPhase): boolean {
  return phase.priceMicros === 0 || phase.paymentMode === 'FreeTrial';
}

function phaseEnd(start: Date, phase: CdvPurchase.PricingPhase): Date | null {
  const match = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/.exec(phase.billingPeriod ?? '');
  const cycles = phase.billingCycles ?? 1;
  if (!match || !Number.isInteger(cycles) || cycles < 1 || !match.slice(1).some(Number)) return null;
  const date = new Date(start);
  // Calendar months/years, clamped at month end (Jan 31 + 1 month = Feb 28).
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCFullYear(date.getUTCFullYear() + Number(match[1] || 0) * cycles);
  date.setUTCMonth(date.getUTCMonth() + Number(match[2] || 0) * cycles);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay) + (Number(match[3] || 0) * 7 + Number(match[4] || 0)) * cycles);
  return Number.isNaN(date.getTime()) ? null : date;
}

function describeOffer(offer: CdvPurchase.Offer): ResolvedPurchaseOffer | null {
  const phases = offer.pricingPhases ?? [];
  const first = phases[0];
  const recurring = phases[phases.length - 1];
  if (!first?.price || !recurring?.price || !(recurring.priceMicros > 0) || isFreePhase(recurring)) return null;
  if (phases.some(phase => !phase.price || !Number.isFinite(phase.priceMicros) || phase.priceMicros < 0)) return null;
  const free = isFreePhase(first);
  const discounted = !free && first.priceMicros > 0 && first.priceMicros < recurring.priceMicros;
  // A paid-only offer called trial7 is not proof of a free trial.
  if (!free && /trial7/.test(offer.id)) return null;
  if (/bestand25/.test(offer.id) && !discounted) return null;
  let chargeAt: Date | null = new Date();
  if (free) {
    for (const phase of phases) {
      if (!isFreePhase(phase)) break;
      chargeAt = chargeAt && phaseEnd(chargeAt, phase);
    }
    if (!chargeAt) return null;
  }
  return {
    kind: free ? 'trial' : discounted ? 'discount' : 'regular',
    todayAmount: first.price,
    firstChargeAt: chargeAt.toISOString(),
    recurringPrice: recurring.price,
    offerId: offer.id,
  };
}

export function resolvePurchaseOffer(
  tier: SubscriptionTier, duration: SubscriptionDuration, options: PurchaseOfferOptions,
): ResolvedPurchaseOffer | null {
  const platform = getCapacitorPlatform();
  const cdv = getCdv();
  const sub = findSubByTierDuration(tier, duration);
  if (!initialized || !platform || !cdv?.store || !sub) return null;
  const android = platform === 'android';
  const productId = android ? options.googleProductId ?? sub.googleProductId ?? GOOGLE_SUB_PRODUCT_ID
    : options.appleProductId ?? sub.appleProductId;
  const basePlan = options.googleBasePlanId ?? sub.googleBasePlanId;
  const product = cdv.store.get(productId, android
    ? ANDROID_CDV_PLATFORM as CdvPurchase.Platform : cdv.Platform.APPLE_APPSTORE);
  const candidates = (product?.offers ?? [])
    .filter(offer => !android || offer.id === basePlan || offer.id.startsWith(`${basePlan}@`)
      || offer.id === `${productId}@${basePlan}` || offer.id.startsWith(`${productId}@${basePlan}@`))
    .map(offer => ({ offer, result: describeOffer(offer) }));
  const desired = options.wantDiscount ? 'discount' : options.wantTrial ? 'trial' : 'regular';
  const chosen = candidates.find(({ offer, result }) => result?.kind === desired
    && (desired !== 'discount' || offer.id === (tier === 'plus' ? 'bestand25' : `bestand25${tier}`) || offer.id.endsWith('@bestand25')))
    ?? (!options.wantDiscount ? candidates.find(({ result }) => result?.kind === 'regular') : undefined);
  if (!chosen?.result) return null;
  resolvedOfferBindings.set(chosen.result, {
    offer: chosen.offer, productId, phases: JSON.stringify(chosen.offer.pricingPhases), tier, duration,
  });
  return Object.freeze(chosen.result);
}

// Wählt das Offer für einen bestimmten Abo-Tarif. Apple ignoriert den base plan
// und liefert das Default-Offer der (tier-spezifischen) productId. Google hat EIN
// Produkt mit mehreren base-plan-Offers → wir nehmen NUR das Offer, dessen id den
// gewünschten base plan enthält. KEIN Fallback auf ein beliebiges anderes Offer:
// das würde den User für den falschen Tarif belasten.
//
// S7 (Befund 2 in belegte/03-frontend.md): wird ein bestimmtes Offer
// (`trial7`, `bestand25`) angefordert und existiert es im Store nicht, dann
// WIRD HIER GEWORFEN — kein stiller Ersatz durch den Basistarif. Der Aufrufer
// (purchaseSubscription) leitet den IapError('unavailable','offer_missing')
// weiter, die UI blendet den Gratis-/Rabatt-Anspruch aus und zeigt den
// normalen Preis mit CTA „Für {price} pro {Laufzeit} abonnieren".
function pickSubscriptionOffer(
  store: CdvPurchase.Store,
  productId: string,
  platformEnum: CdvPurchase.Platform,
  googleBasePlanId: string,
  isAndroid: boolean,
  googleOfferId?: string,
): CdvPurchase.Offer {
  const product = store.get(productId, platformEnum);
  if (!product) throw new IapError('unavailable', 'offer_missing');
  if (!isAndroid) {
    const offer = product.getOffer();
    if (!offer || (googleOfferId === 'trial7' && !offer.pricingPhases?.some(isFreePhase))
      || (googleOfferId === 'bestand25' && describeOffer(offer)?.kind !== 'discount')) {
      throw new IapError('unavailable', 'offer_missing');
    }
    return offer;
  }
  const offers = product.offers ?? [];
  const basePlanOffers = offers.filter((o) => typeof o.id === 'string' && o.id.includes(googleBasePlanId));
  if (googleOfferId) {
    const exact = basePlanOffers.find((o) => typeof o.id === 'string' && o.id.includes(googleOfferId));
    if (exact && (googleOfferId !== 'trial7' || exact.pricingPhases?.some(isFreePhase))) return exact;
    // S7: ein angefordertes Rabatt-/Trial-Offer existiert im Store nicht →
    // NICHT still auf den Basistarif zurueckfallen. Wirft offer_missing,
    // damit die UI den Rabatt-Anspruch ausblendet.
    throw new IapError('unavailable', 'offer_missing');
  }
  const fallback = basePlanOffers.find((o) => !/trial7|bestand25/.test(String(o.id)));
  if (!fallback) throw new IapError('unavailable', 'offer_missing');
  return fallback;
}

// Wartet auf das (exakte) Offer eines Abo-Tarifs. Offers laden nach
// store.initialize() asynchron nach; pollt bis maxMs. Liefert undefined, wenn
// das Produkt/Offer nie erscheint. Wenn `googleOfferId` angefordert wurde und
// der Basistarif ohne dieses Offer geladen ist, wirft pickSubscriptionOffer
// `offer_missing` — der Aufrufer kann den Fehler abfangen und ohne
// Trial/Rabatt-Pfad kaufen.
async function waitForSubscriptionOffer(
  store: CdvPurchase.Store,
  productId: string,
  platformEnum: CdvPurchase.Platform,
  googleBasePlanId: string,
  isAndroid: boolean,
  googleOfferId?: string,
  maxMs = 10000,
  intervalMs = 150,
): Promise<CdvPurchase.Offer | undefined> {
  try {
    return pickSubscriptionOffer(store, productId, platformEnum, googleBasePlanId, isAndroid, googleOfferId);
  } catch (err) {
    if (err instanceof IapError && err.code === 'unavailable' && err.message === 'offer_missing') {
      // Erst warten — vielleicht laedt das Produkt/Offer noch nach. Erst
      // nach maxMs definitiv aufgeben (kein stiller Fallback).
      triggerStoreRefresh(store);
      const maxTries = Math.max(1, Math.ceil(maxMs / intervalMs));
      for (let i = 0; i < maxTries; i += 1) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, intervalMs));
        try {
          return pickSubscriptionOffer(store, productId, platformEnum, googleBasePlanId, isAndroid, googleOfferId);
        } catch (retryErr) {
          if (retryErr instanceof IapError && retryErr.code === 'unavailable' && retryErr.message === 'offer_missing') continue;
          throw retryErr;
        }
      }
      // Nach den Wiederholungen muss die Paywall das fehlende Angebot erkennen,
      // ohne versehentlich den Basistarif zu kaufen.
      if (googleOfferId) throw err;
      return undefined;
    }
    throw err;
  }
}

// Liefert den lokalisierten Anzeige-Preis eines Abo-Tarifs aus dem geladenen
// Store-Offer (echter App-Store-/Play-Preis im jeweiligen Storefront). Liefert
// null, wenn das Offer (noch) nicht geladen ist — der Aufrufer fällt dann auf den
// EUR-Basispreis zurück. Synchron (liest den aktuellen Store-Zustand).
export function getSubscriptionStorePrice(tier: SubscriptionTier, duration: SubscriptionDuration): string | null {
  if (!initialized) return null;
  const cdvPlatform = getCapacitorPlatform();
  if (!cdvPlatform) return null;
  const sub = findSubByTierDuration(tier, duration);
  if (!sub) return null;
  const cdv = getCdv();
  const store = cdv?.store;
  const Platform = cdv?.Platform;
  if (!store || !Platform) return null;
  const isAndroid = cdvPlatform === 'android';
  const platformEnum: CdvPurchase.Platform = isAndroid
    ? (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform)
    : Platform.APPLE_APPSTORE;
  const storeProductId = isAndroid ? sub.googleProductId ?? GOOGLE_SUB_PRODUCT_ID : sub.appleProductId;
  let offer: CdvPurchase.Offer | undefined;
  try {
    offer = pickSubscriptionOffer(store, storeProductId, platformEnum, sub.googleBasePlanId, isAndroid);
  } catch {
    return null;
  }
  if (!offer) return null;
  const phases = offer.pricingPhases ?? [];
  // Wiederkehrende Phase = letzte Phase (etwaige Intro-/Trial-Phasen stehen davor).
  const phase = phases[phases.length - 1] ?? phases[0];
  const price = phase?.price;
  return typeof price === 'string' && price ? price : null;
}

// ─── Apple: appStoreReceipt aus den lokalen Receipts ziehen ─────────────────

interface AppleNativeReceipt {
  appStoreReceipt?: string;
  bundleIdentifier?: string;
  bundleShortVersion?: string;
}

interface AppleAppStoreReceipt {
  nativeData?: AppleNativeReceipt;
}

interface GooglePlayTransaction extends CdvPurchase.Transaction {
  nativePurchase?: { purchaseToken?: string };
  purchaseToken?: string;
  isAcknowledged?: boolean;
}

function readAppleAppStoreReceipt(): string | null {
  const cdv = getCdv();
  const store = cdv?.store;
  if (!store) return null;
  const receipts = store.localReceipts;
  if (!Array.isArray(receipts)) return null;
  for (const r of receipts) {
    if (!r || typeof r !== 'object') continue;
    const native = (r as AppleAppStoreReceipt).nativeData;
    if (native?.appStoreReceipt) return native.appStoreReceipt;
  }
  return null;
}

function readGooglePurchaseToken(transaction: CdvPurchase.Transaction): string | null {
  const gp = transaction as GooglePlayTransaction;
  if (typeof gp.purchaseToken === 'string' && gp.purchaseToken) return gp.purchaseToken;
  const native = gp.nativePurchase;
  if (native && typeof native === 'object' && typeof native.purchaseToken === 'string') {
    return native.purchaseToken;
  }
  return null;
}

// One receipt can contain several subscription items. Deduplicate by token,
// retain finished subscriptions, and ignore pending/expired transactions.
export function readGoogleSubscriptionTokens(): string[] {
  const receipts = getCdv()?.store?.localReceipts;
  if (!Array.isArray(receipts)) return [];
  const transactions = receipts.flatMap((r) => r.transactions || [])
    .filter((tx) => tx.products?.some((p) => GOOGLE_SUB_IDS.has(p.id))
      && !tx.isPending && (!tx.expirationDate || tx.expirationDate.getTime() > Date.now()))
    .sort((a, b) => (b.purchaseDate?.getTime() || 0) - (a.purchaseDate?.getTime() || 0));
  return [...new Set(transactions.map(readGooglePurchaseToken).filter((token): token is string => !!token))];
}

export type PurchaseStatus = { attemptId?: string; state: 'idle' | 'confirming' | 'delayed' | 'owner_conflict' | 'already_owned' | 'verified' };
let purchaseStatus: PurchaseStatus = { state: 'idle' };
const statusListeners = new Set<() => void>();
export const getPurchaseStatus = () => purchaseStatus;
export function subscribePurchaseStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => { statusListeners.delete(listener); };
}
function updatePurchaseStatus(state: PurchaseStatus['state'], attemptId?: string): void {
  purchaseStatus = { state, attemptId };
  statusListeners.forEach(listener => listener());
}
const unresolvedAttempts = new Map<string, { attempt: PurchaseAttempt; funnel?: FunnelEventData }>();
const verifyingTransactions = new Set<string>();
const completedTransactions = new Set<string>();
const ignoredRecoveryTransactions = new Set<string>();
const appleRecoveryGroups = new Map<string, Promise<boolean>>();
const appleRecoveryTransactions = new Map<string, Map<string, CdvPurchase.Transaction>>();
let appleRecoveryQueue: Promise<unknown> = Promise.resolve();

async function recoverTransaction(transaction: CdvPurchase.Transaction, productId: string, expectedCoins: number): Promise<void> {
  if (restoringSubscriptions) return;
  const key = transactionKey(transaction);
  if (completedTransactions.has(key) || ignoredRecoveryTransactions.has(key)) return;
  if ((transaction.platform ?? platformToCdv(activePlatform ?? 'android')) !== 'ios-appstore') {
    // Google liefert auch bestätigte aktive Abos dauerhaft als APPROVED.
    if (isSubscriptionProductId(productId) && (transaction as GooglePlayTransaction).isAcknowledged) return;
    await verifyAndFinish(transaction, productId, expectedCoins, { hasPending: false, recovery: true, background: true });
    return;
  }
  // Ein SK1-Receipt enthält die gesamte Historie. Pro Receipt/Produkt reicht ein Verify;
  // verschiedene Produkte laufen serialisiert, auch wenn approved parallel eintrifft.
  const groupKey = JSON.stringify([readAppleAppStoreReceipt(), productId]);
  const transactions = appleRecoveryTransactions.get(groupKey) ?? new Map<string, CdvPurchase.Transaction>();
  transactions.set(key, transaction);
  appleRecoveryTransactions.set(groupKey, transactions);
  let job = appleRecoveryGroups.get(groupKey);
  if (!job) {
    job = appleRecoveryQueue.then(async () => {
      await verifyAndFinish(transaction, productId, expectedCoins, { hasPending: false, recovery: true, background: true, recoveryGroup: groupKey });
      return completedTransactions.has(key);
    });
    appleRecoveryGroups.set(groupKey, job);
    appleRecoveryQueue = job.catch(() => false);
  }
  const finished = await job;
  if (finished && !completedTransactions.has(key)) {
    try { await transaction.finish(); completedTransactions.add(key); } catch { /* Beim nächsten Callback erneut abschließen. */ }
  }
}

async function finishRecoveryTransactions(transaction: CdvPurchase.Transaction, groupKey?: string): Promise<void> {
  const transactions = groupKey ? appleRecoveryTransactions.get(groupKey)?.values() : undefined;
  let failed = false;
  for (const tx of transactions ?? [transaction]) {
    const key = transactionKey(tx);
    if (completedTransactions.has(key)) continue;
    try { await tx.finish(); completedTransactions.add(key); }
    catch { failed = true; }
  }
  if (groupKey) appleRecoveryGroups.set(groupKey, Promise.resolve(true));
  if (failed) throw new Error('finish_failed');
}
const transactionObjects = new WeakMap<object, string>();
function transactionKey(transaction: CdvPurchase.Transaction): string {
  let id = transaction.transactionId || readGooglePurchaseToken(transaction);
  if (!id) {
    id = transactionObjects.get(transaction) ?? createAttemptId();
    transactionObjects.set(transaction, id);
  }
  return `${transaction.platform ?? platformToCdv(activePlatform ?? 'android')}:${id}`;
}
function localTransactionKeys(): Set<string> {
  return new Set((getCdv()?.store.localReceipts ?? []).flatMap(receipt => receipt.transactions ?? []).map(transactionKey));
}
async function invalidateMembership(): Promise<void> {
  const appQueryClient = await getAppQueryClient();
  await appQueryClient.invalidateQueries({ queryKey: ['membership'] });
  await appQueryClient.invalidateQueries({ queryKey: ['coins'] });
}

// Nach Genehmigung bleibt der Kauf gesperrt, bis der Server den Anspruch sicher kennt.
async function pollVerification(attempt: PurchaseAttempt): Promise<VerificationResponse> {
  updatePurchaseStatus('confirming', attempt.id);
  const started = Date.now();
  for (const at of [3000, 6000, 12000, 24000]) {
    await new Promise<void>(resolve => window.setTimeout(resolve, Math.max(0, at - (Date.now() - started))));
    try {
      const response = await getVerificationStatus(attempt.id);
      if (response.status === 'verified' || response.status === 'failed') return response;
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) throw error;
      // Auch alte Server ohne Statusroute dürfen keine zweite Belastung auslösen.
    }
  }
  updatePurchaseStatus('delayed', attempt.id);
  return { ok: true, status: 'pending' };
}
interface DeferredVerification {
  retries: number;
  timer?: number;
  run: () => Promise<void>;
}
const deferredVerifications = new Map<string, DeferredVerification>();
export function resumePendingVerifications(): void {
  for (const job of pendingRestores.values()) {
    if (job.timer) window.clearTimeout(job.timer);
    job.timer = undefined;
    void job.run();
  }
  for (const [key, job] of deferredVerifications) {
    if (verifyingTransactions.has(key)) continue;
    if (job.timer) window.clearTimeout(job.timer);
    job.timer = undefined;
    void job.run();
  }
}
function deferVerification(transaction: CdvPurchase.Transaction, productId: string, expectedCoins: number,
  attempt: PurchaseAttempt, funnel: FunnelEventData | undefined, pollOnly: boolean, recoveryGroup?: string): void {
  const key = transactionKey(transaction);
  const job = deferredVerifications.get(key) ?? { retries: 0, run: async () => {} };
  job.run = () => {
    const run = () => verifyAndFinish(transaction, productId, expectedCoins, {
      hasPending: pending.get(productId)?.attempt?.id === attempt.id, recovery: true, attempt, funnel, pollOnly, background: true, recoveryGroup,
    });
    if (!recoveryGroup) return run();
    const queued = appleRecoveryQueue.then(run);
    appleRecoveryQueue = queued.catch(() => undefined);
    return queued;
  };
  if (job.timer) window.clearTimeout(job.timer);
  if (job.retries < 5) {
    job.retries += 1;
    job.timer = window.setTimeout(() => { job.timer = undefined; void job.run(); }, 60_000);
  }
  deferredVerifications.set(key, job);
}
function clearDeferredVerification(key: string): void {
  const job = deferredVerifications.get(key);
  if (job?.timer) window.clearTimeout(job.timer);
  deferredVerifications.delete(key);
}
async function verifyAndFinish(
  transaction: CdvPurchase.Transaction, productId: string, expectedCoins: number,
  options: { hasPending: boolean; recovery?: boolean; attempt?: PurchaseAttempt; funnel?: FunnelEventData; pollOnly?: boolean; background?: boolean; recoveryGroup?: string },
): Promise<void> {
  const key = transactionKey(transaction);
  if (verifyingTransactions.has(key) || completedTransactions.has(key) || ignoredRecoveryTransactions.has(key)) return;
  verifyingTransactions.add(key);
  const candidateEntry = options.hasPending ? pending.get(productId) : undefined;
  const entry = candidateEntry && (!options.attempt || candidateEntry.attempt?.id === options.attempt.id) ? candidateEntry : undefined;
  const held = unresolvedAttempts.get(key);
  const attempt = options.attempt ?? entry?.attempt ?? held?.attempt ?? createPurchaseAttempt({ product_id: productId });
  const funnel = options.funnel ?? entry?.funnel ?? held?.funnel;
  const silentRecovery = options.background === true && !held && !entry;
  if (entry) { entry.approved = true; window.clearTimeout(entry.timeoutId); }
  if (!silentRecovery) unresolvedAttempts.set(key, { attempt, funnel });
  if (!options.background) updatePurchaseStatus('confirming', attempt.id);
  setPurchasePhase(attempt, 'approved');
  const context = { attempt_id: attempt.id, ...(options.recovery ? { recovery: true } : {}) };
  const platform = transaction.platform ?? platformToCdv(activePlatform === 'ios' ? 'ios' : 'android');
  try {
    let response: VerificationResponse | undefined;
    setPurchasePhase(attempt, 'verify');
    try {
      if (options.pollOnly) {
        try { response = await getVerificationStatus(attempt.id); }
        catch (error) {
          // Bei alten Servern oder verlorenem Erstrequest denselben Verify idempotent erneut senden.
          if (!(error instanceof ApiError) || error.status !== 404) throw error;
        }
      }
      if (!response && platform === 'ios-appstore') {
        let receipt = readAppleAppStoreReceipt();
        for (const delay of [500, 500, 500, 3000, 6000, 12000, 24000]) {
          if (receipt) break;
          await new Promise<void>(resolve => window.setTimeout(resolve, delay));
          receipt = readAppleAppStoreReceipt();
        }
        if (!receipt) {
          // Ohne gesendetes Receipt kennt der Server noch keine attempt_id. Lokal weiter warten.
          if (!silentRecovery) updatePurchaseStatus('delayed', attempt.id);
          deferVerification(transaction, productId, expectedCoins, attempt, funnel, false, options.recoveryGroup);
          if (entry) resolvePending(productId, { ok: true, coins: 0, status: 'pending' });
          return;
        }
        response = await verifyAppleReceipt(productId, receipt, context);
      } else if (!response) {
        const token = readGooglePurchaseToken(transaction);
        if (!token) throw new IapError('verify_failed', 'google_token_missing', { phase: 'verify' });
        response = await verifyGooglePurchase(productId, token, isSubscriptionProductId(productId) ? 'SUBS' : 'INAPP',
          entry?.recovery || options.recovery ? undefined : googleSubBasePlanInFlight ?? undefined, context);
      }
    } catch (error) {
      const failure = fromVerificationError(error);
      if (classifyPurchaseFailure(failure) !== 'verify_pending' && !options.pollOnly) throw failure;
      if (options.pollOnly && failure.httpStatus === 409) throw failure;
      response = { ok: true, status: 'pending' };
    }
    if (!response) throw new IapError('verify_failed', 'verify_response_missing');
    if (response.status === 'pending') {
      if (!silentRecovery) unresolvedAttempts.set(key, { attempt, funnel });
      if (!options.background) response = await pollVerification(attempt);
      if (response.status === 'pending') {
        deferVerification(transaction, productId, expectedCoins, attempt, funnel, true, options.recoveryGroup);
        if (entry) resolvePending(productId, { ok: true, coins: 0, status: 'pending' });
        return;
      }
    }
    if (!response.ok || response.status === 'failed') {
      const failure = fromVerificationError(new ApiError(200, response));
      if (response.status === 'failed') {
        failure.retryable = false;
        if (silentRecovery) ignoredRecoveryTransactions.add(key);
      }
      throw failure;
    }
    clearDeferredVerification(key);
    setPurchasePhase(attempt, 'finish');
    try {
      await finishRecoveryTransactions(transaction, options.recoveryGroup);
    } catch { /* Der nächste approved-Callback versucht finish erneut; Verify bleibt idempotent. */ }
    if (unresolvedAttempts.get(key)?.attempt.id === attempt.id) unresolvedAttempts.delete(key);
    if (!silentRecovery) updatePurchaseStatus(options.background ? 'verified' : 'idle', attempt.id);
    if (funnel) trackFunnel('purchase_verified', { ...funnel, meta: purchaseDiagnostics(attempt) });
    if (entry) resolvePending(productId, { ok: true, status: 'verified', coins: response.coins ?? expectedCoins, balance: response.balance });
    await invalidateMembership();
  } catch (error) {
    clearDeferredVerification(key);
    const failure = fromVerificationError(error);
    if (unresolvedAttempts.get(key)?.attempt.id === attempt.id) unresolvedAttempts.delete(key);
    if (silentRecovery) {
      const ownerConflict = classifyPurchaseFailure(failure) === 'owner_conflict';
      if (ownerConflict) ignoredRecoveryTransactions.add(key);
      else if (failure.httpStatus && failure.httpStatus >= 400 && failure.httpStatus < 500 && failure.retryable === false) {
        // Eine fehlende Sitzung ist kein endgültig ungültiger Store-Beleg.
        // F94: Testkauf, den der Server in dieser Umgebung bewusst nicht gutschreibt →
        // abschließen, damit der Store ihn nicht bei jedem App-Start erneut liefert.
        if (['invalid_request', 'subscription_token_replaced', 'store_state_not_entitled', 'sandbox_purchase_rejected'].includes(failure.errorCode ?? '')) {
          try { await finishRecoveryTransactions(transaction, options.recoveryGroup); }
          catch { ignoredRecoveryTransactions.add(key); }
        } else ignoredRecoveryTransactions.add(key);
      } else if (failure.retryable !== false) {
        deferVerification(transaction, productId, expectedCoins, attempt, funnel, false, options.recoveryGroup);
      }
    } else if (classifyPurchaseFailure(failure) === 'owner_conflict') updatePurchaseStatus('owner_conflict', attempt.id);
    else updatePurchaseStatus('idle');
    if (entry) rejectPending(productId, failure);
  } finally {
    verifyingTransactions.delete(key);
  }
}

async function handleApproved(
  transaction: CdvPurchase.Transaction,
  products: IapProduct[],
): Promise<void> {
  const productId = transaction.products?.[0]?.id;
  // Review-Befund 75-kauf (Blocker): Die von cordova-plugin-purchase auf iOS
  // erzeugte VIRTUELLE App-Transaktion (StoreKit-1-Bridge, `needAppReceipt`
  // per Default an; www/store.js SKApplicationReceipt) ist KEIN Restore-
  // Artefakt, sondern steht bei JEDEM 'purchased'-Callback des Plugins an
  // erster Stelle in _receipt.transactions und wird alle >60s erneut als
  // approved gemeldet (_processUpdatedReceipts), solange ein Kauf-Zyklus
  // läuft. Ihre transactionId ist entweder die feste Konstante
  // 'appstore.application' oder — vor dem ersten echten Callback —
  // `virtual.<productId>` (virtualTransactionId in store.js), NIE eine echte
  // Kauf-ID. Sie NICHT anhand der productId (== Bundle-ID) erkennen: die
  // App-Transaktion trägt bereits die Bundle-ID als productId, aber ein
  // echter, wartender Kauf hat eine ANDERE productId — der alte Code lehnte
  // deshalb bei jeder App-Transaktion den zuletzt wartenden ECHTEN Kauf ab
  // (rejectPending(lastKey)), auch wenn lastKey gar nicht die Bundle-ID war.
  // Fix: eigener Zweig NUR für die App-Transaktion selbst — finish() setzt
  // laut store.js nur den lokalen Zustand (state=FINISHED) und beendet die
  // Wiederholmeldungen, pending bleibt unangetastet, jeder andere wartende
  // Kauf läuft normal weiter. productId === APP_BUNDLE_ID allein reicht als
  // Erkennungsmerkmal: kein reales SUBSCRIPTION_PRODUCTS-/Coin-Produkt trägt
  // die nackte Bundle-ID als productId. Die transactionId-Konstante wird
  // zusätzlich geprüft, weil sie schon VOR dem ersten Receipt-Refresh gilt.
  if (transaction.transactionId === 'appstore.application' || productId === APP_BUNDLE_ID) {
    void transaction.finish().catch(() => undefined);
    return;
  }
  if (!productId) return;
  const entry = pending.get(productId);
  if (transaction.platform && transaction.platform !== platformToCdv(entry?.platform ?? activePlatform ?? 'android')) return;
  const isOldTransaction = entry?.existingTransactions?.has(transactionKey(transaction)) && !entry.recovery;
  if (!entry || isOldTransaction) {
    await recoverTransaction(transaction, productId, getProductCoinAmount(products, productId));
    return;
  }
  await verifyAndFinish(transaction, productId, getProductCoinAmount(products, productId), {
    hasPending: !!entry && !isOldTransaction,
    recovery: !entry || !!isOldTransaction || entry.recovery,
    // Ein Altbeleg darf keinen neuen Kauf allein aufgrund der Produkt-ID abschließen.
    attempt: isOldTransaction ? createPurchaseAttempt({ product_id: productId }) : undefined,
  });
}

function handleStoreError(error: { code?: unknown; message?: string; productId?: string | null; platform?: string | null; attempt_id?: string; billingCode?: number }): void {
  lastStoreError = String(error.code ?? '').slice(0, 90);
  const failure = fromStoreError(error);
  // Lade-/Refresh-Fehler sind auch ohne Produkt-ID kein Checkout-Ergebnis.
  if ([6777001, 6777002, 6777004, 6777011, 6777019].includes(Number(error.code))
    || /^(SETUP|LOAD|LOAD_RECEIPTS|REFRESH_RECEIPTS|REFRESH)$/.test(String(error.code))) return;
  const active = [...pending.entries()][0];
  if (!active) return;
  const [productId, entry] = active;
  if (entry.approved || (error.productId && error.productId !== productId)
    || (error.platform && error.platform !== platformToCdv(entry.platform ?? activePlatform ?? 'android'))
    || (error.attempt_id && error.attempt_id !== entry.attempt?.id)) return;
  // Ohne Produktbezug nur explizite Zahlungsfehler während des offenen Sheets zuordnen.
  if (!error.productId && !['user_cancelled', 'already_owned', 'payment_declined'].includes(classifyPurchaseFailure(failure))) return;
  void settleOrderFailure(productId, entry, failure);
}
async function settleOrderFailure(productId: string, entry: PendingEntry, error: unknown): Promise<void> {
  if (pending.get(productId) !== entry || entry.approved) return;
  const failure = fromStoreError(error);
  if (classifyPurchaseFailure(failure) === 'already_owned' && isSubscriptionProductId(productId)) {
    if (entry.recovery) return;
    entry.recovery = true;
    window.clearTimeout(entry.timeoutId);
    updatePurchaseStatus('already_owned', entry.attempt?.id);
    try {
      const restoreError = await getCdv()!.store.restorePurchases();
      if (restoreError) throw fromStoreError(restoreError);
      const receipts = getCdv()!.store.localReceipts ?? [];
      const transactions = receipts.flatMap(receipt => receipt.transactions ?? [])
        .filter(tx => !tx.isPending && tx.products?.some(product => product.id === productId));
      for (const tx of transactions) await verifyAndFinish(tx, productId, 0, { hasPending: true, recovery: true });
      if (pending.get(productId) === entry && !entry.approved) throw new IapError('verify_failed', 'restore_failed');
      await invalidateMembership();
    } catch (caught) {
      const error = caught instanceof IapError ? caught : fromVerificationError(caught);
      updatePurchaseStatus(classifyPurchaseFailure(error) === 'owner_conflict' ? 'owner_conflict' : 'idle');
      rejectPending(productId, error);
    }
    return;
  }
  rejectPending(productId, failure);
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Einmalig beim Native-Start aufrufen. No-Op auf Web. Idempotent.
 *
 * Initialisiert den CdvPurchase-Store mit den uebergebenen Produkten und
 * laedt ggf. vorhandene Receipts (fuer Restore-Flow). Fehler werden
 * tolerant geloggt und nicht geworfen — der App-Start darf nicht
 * blockieren, wenn der Store temporaer nicht verfuegbar ist.
 */
export async function initIap(products: IapProduct[]): Promise<void> {
  if (initialized) return;
  if (purchasesBlockedByEnvironment()) { lastIapInitError = 'beta_environment'; return; }
  // Produktliste für einen späteren On-Demand-Reinit merken (auch wenn diese
  // Init gleich noch scheitert).
  if (products.length > 0) lastProducts = products;
  if (!await isNativeCapacitorPlatform()) { lastIapInitError = 'not_native'; return; }
  if (typeof window === 'undefined') return;

  if (!initPromise) {
    initPromise = (async () => {
      if (initialized) return;

      const cdvPlatform = getCapacitorPlatform();
      if (cdvPlatform !== 'ios' && cdvPlatform !== 'android') { lastIapInitError = `bad_platform:${cdvPlatform}`; return; }
      activePlatform = cdvPlatform;

      // cordova-plugin-purchase ist ein CORDOVA-Plugin: Capacitor injiziert das
      // Plugin-JS beim Start und legt `window.CdvPurchase` global an (clobbers).
      // KEIN ESM-Import — der wuerde nur eine modul-gescopte Kopie ausfuehren,
      // die `window.CdvPurchase` NICHT setzt (store.js nutzt ein top-level
      // `var CdvPurchase`, das im Modul-Scope landet). Wir warten daher auf den
      // vom Cordova-Bridge bereitgestellten globalen Namespace; er kann minimal
      // verzoegert nach dem ersten Render verfuegbar werden.
      const cdv = await waitForCdvPurchase();
      if (!cdv) {
        lastIapInitError = 'cdv_missing_after_wait';
        console.warn('[iap] window.CdvPurchase nicht verfuegbar — Cordova-Plugin nicht injiziert?');
        return;
      }
      const { store, Platform, ProductType } = cdv;
      const platformId = platformToCdv(cdvPlatform);
      // Plattform-Enum aus CdvPurchase waehlen, damit die store.register-API
      // den exakt gleichen Identifier (z.B. 'ios-appstore') bekommt.
      const platformEnum: CdvPurchase.Platform = platformId === 'ios-appstore'
        ? Platform.APPLE_APPSTORE
        : (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform);

      // Abo-Produkte registrieren: Apple hat 8 separate productIds, Google EIN
      // Produkt (`flat`) mit base plans als Offers.
      const subscriptionRegistrations = cdvPlatform === 'ios'
        ? SUBSCRIPTION_PRODUCTS.map((s) => ({
          id: s.appleProductId,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: platformEnum,
        }))
        : [...GOOGLE_SUB_IDS].map((id) => ({
          id,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: platformEnum,
        }));

      const registrations = [
        ...products.map((p) => ({
          id: p.productId,
          type: ProductType.CONSUMABLE,
          platform: platformEnum,
        })),
        ...subscriptionRegistrations,
      ];
      try {
        store.register(registrations);
      } catch (err) {
        lastIapInitError = `register_threw:${err instanceof Error ? err.message : String(err)}`;
        console.warn('[iap] store.register fehlgeschlagen:', err);
        return;
      }
      const regN = storeProductCount(store);
      lastInitDetail = `inN=${registrations.length} regN=${regN}`;

      // approved-Handler: nimmt das aktuelle Produkt-Set per Closure mit, damit
      // wir die erwartete Coin-Menge fuer den Erfolgs-Toast haben.
      const productList = products;
      if (!handlersRegistered) {
        store.when().approved((transaction: CdvPurchase.Transaction) => {
          // Niemals unhandled werfen — CdvPurchase loggt sonst eine "logCallbackException".
          void handleApproved(transaction, productList);
        });

        // error-Handler: cancellable / store_error unterscheiden.
        store.error((err: { code?: unknown; message?: string }) => {
          handleStoreError(err);
        });
        window.addEventListener('focus', resumePendingVerifications);
        document.addEventListener('resume', resumePendingVerifications);
        document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') resumePendingVerifications(); });
        handlersRegistered = true;
      }
      try {
        if (storeInitializationCalled) {
          // initialize() ist im Plugin ein One-Shot; ein zweites [] wäre kein Erfolg.
          const previousInterval = store.minTimeBetweenUpdates;
          try { store.minTimeBetweenUpdates = 0; await store.update(); }
          finally { store.minTimeBetweenUpdates = previousInterval; }
          const adapter = store.getAdapter?.(platformEnum);
          const ready = store.isReady && adapter?.ready && store.products.some(product =>
            product.platform === platformEnum && product.canPurchase && product.offers.length > 0);
          if (!ready) return;
        } else {
          storeInitializationCalled = true;
          const initErrors = await store.initialize([platformEnum]);
          if (Array.isArray(initErrors) && initErrors.length > 0) {
            lastIapInitError = `initialize_errors:${initErrors.map(error => String(error.code)).join(',').slice(0, 120)}`;
            return;
          }
        }
      } catch (err) {
        lastIapInitError = `initialize_threw:${err instanceof Error ? err.message : String(err)}`;
        console.warn('[iap] store.initialize fehlgeschlagen:', err);
        return;
      }

      lastInitDetail = `${lastInitDetail} postN=${storeProductCount(store)} plat=${platformId}`;
      lastIapInitError = null;
      initialized = true;
      detailsLoadedAt = Date.now();
    })().finally(() => {
      // FIX 03-P2-11: War die Init NICHT erfolgreich (initialized bleibt false —
      // Plugin nicht bereit / register / initialize fehlgeschlagen), initPromise
      // zurücksetzen, damit ein späterer Aufruf einen ZWEITEN Versuch bekommt statt
      // für die ganze Session an der einmal fehlgeschlagenen Promise zu hängen. Zudem
      // begrenzt automatisch neu versuchen (Cordova-Plugin lädt auf langsamen Starts
      // evtl. verzögert).
      if (!initialized) {
        initPromise = null;
        if (iapInitAttempts < IAP_INIT_MAX_ATTEMPTS) {
          iapInitAttempts += 1;
          setTimeout(() => { void initIap(products); }, 3000);
        }
      }
    });
  }

  return initPromise;
}

/**
 * Stellt sicher, dass die uebergebenen Coin-Produkte beim CdvPurchase-Store
 * registriert sind. Idempotent und tolerant — wirft nicht.
 *
 * Fresh-Install-Bug: Beim App-Boot laeuft getCoinIapPackages() VOR dem Login
 * → 401 → leere Coin-Liste → initIap registriert 0 Coin-Produkte. Danach
 * wurde die Liste NIE aktualisiert → der erste Kauf-Tap scheitert mit
 * "offer_not_loaded", obwohl das Produkt serverseitig existiert. Diese
 * Funktion schliesst die Luecke, indem sie:
 *   (a) bei noch nicht initialisiertem Store einen (erneuten) initIap mit der
 *       jetzt bekannten Liste anstoesst (Retry-Zaehler zurueckgesetzt, damit
 *       ein blockierter Zaehler einen frischen Versuch nicht abwuergt), und
 *   (b) bei bereits initialisiertem Store nur die FEHLENDEN Coin-Produkte per
 *       store.register([...]) nachregistriert und einmalig store.update()
 *       anstoesst (best-effort). Abos bleiben unveraendert — die sind bereits
 *       im Boot-Init korrekt erfasst worden.
 *
 * lastProducts wird auf die Vereinigungsmenge gehoben, damit der
 * On-Demand-Reinit im Kauf-Pfad die volle Produktliste kennt.
 *
 * Kein UI-Toast, kein Wurf — Fehler werden tolerant geloggt.
 */
export async function ensureProductsRegistered(products: IapProduct[]): Promise<void> {
  // Web/PWA / SSR — keine Stores, kein Plugin, no-op.
  if (purchasesBlockedByEnvironment()) { lastIapInitError = 'beta_environment'; return; }
  if (!await isNativeCapacitorPlatform()) return;
  if (typeof window === 'undefined') return;

  // Vereinigungsmenge in lastProducts: lastProducts ∪ products (per productId,
  // ohne Duplikate). Nur mutieren, wenn die neue Liste nicht leer ist — sonst
  // koennte ein spaeterer Aufruf mit [] eine bereits gefuellte Liste ueberschreiben.
  if (products.length > 0) {
    const byId = new Map<string, IapProduct>();
    for (const p of lastProducts) byId.set(p.productId, p);
    for (const p of products) {
      const existing = byId.get(p.productId);
      // Existierenden Eintrag behalten (koennte reichere Metadaten haben);
      // sonst den neuen uebernehmen.
      byId.set(p.productId, existing ?? p);
    }
    lastProducts = Array.from(byId.values());
  }

  // Fall (a): Store wurde noch nie erfolgreich initialisiert (Boot-Init
  // gescheitert ODER Aufruf passierte vor dem Boot-Init). Mit der jetzt
  // bekannten Liste neu starten. iapInitAttempts auf 0, weil ein verbrauchter
  // Retry-Zaehler einen frischen Versuch sonst blockieren wuerde.
  if (!initialized) {
    iapInitAttempts = 0;
    await initIap(lastProducts);
    // KEIN hartes return: Lief der Boot-Init beim Aufruf bereits als in-flight-
    // Promise (Race: Login-Effekt vor Abschluss des Boot-Inits), hat initIap nur
    // die ALTE Promise gejoint — deren register()-Lauf kannte die frisch
    // uebergebenen Packs noch nicht. Deshalb unten per Fall (b) pruefen, ob
    // trotz erfolgreicher Init noch Produkte fehlen, und sie nachregistrieren.
    if (!initialized) return;
  }

  // Fall (b): Store ist initialisiert — nur die Coin-Produkte nachregistrieren,
  // die im Store noch fehlen. Abos sind bereits registriert (Boot-Init) und
  // werden hier NICHT angefasst.
  const cdv = getCdv();
  if (!cdv) return;
  const { store, Platform, ProductType } = cdv;
  const cdvPlatform = getCapacitorPlatform();
  if (cdvPlatform !== 'ios' && cdvPlatform !== 'android') return;
  const platformEnum: CdvPurchase.Platform = cdvPlatform === 'ios'
    ? Platform.APPLE_APPSTORE
    : (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform);

  const missing = lastProducts.filter(
    (p) => !store.get(p.productId, platformEnum),
  );
  if (missing.length === 0) return;

  try {
    // Typen aus store.d.ts: register(product: IRegisterProduct | ...[]): void.
    // productId, type, platform sind Pflicht; wir nutzen CONSUMABLE analog zum
    // Boot-Init-Pfad.
    store.register(missing.map((p) => ({
      id: p.productId,
      type: ProductType.CONSUMABLE,
      platform: platformEnum,
    })));
  } catch (err) {
    console.warn('[iap] ensureProductsRegistered: store.register fehlgeschlagen:', err);
    return;
  }

  // Best-effort: einmal store.update() anstossen, damit die nachregistrierten
  // Produkte moeglichst schnell vom Adapter geladen werden. Fehler werden
  // geschluckt — der Kauf-Pfad pollt ohnehin selbst auf das Offer.
  try {
    const updater = (store as unknown as { update?: () => Promise<void> }).update;
    if (typeof updater === 'function') await updater();
  } catch (err) {
    console.warn('[iap] ensureProductsRegistered: store.update fehlgeschlagen:', err);
  }
}

// App Review 2.3.10: Der Android-Store-Identifier ('android-playstore', identisch
// mit CdvPurchase.Platform.GOOGLE_PLAY) wird zur Laufzeit per Array.join gebaut,
// damit weder der Identifier noch 'GOOGLE_PLAY' wörtlich im iOS-Binary stehen.
// Laufzeitwert bleibt EXAKT 'android-playstore' → Android-Käufe unverändert.
const ANDROID_CDV_PLATFORM = ['android', 'playstore'].join('-');

function platformToCdv(platform: 'ios' | 'android'): string {
  return platform === 'ios' ? 'ios-appstore' : ANDROID_CDV_PLATFORM;
}

/**
 * Loest einen Kauf aus. Resolved nach serverseitiger Gutschrift; wirft
 * IapError bei Abbruch/Fehler. Auf Web wirft es IapError('unavailable').
 */
export async function purchaseCoins(productId: string): Promise<IapPurchaseResult> {
  if (purchasesBlockedByEnvironment()) {
    throw new IapError('unavailable', 'beta_environment');
  }
  if (!await isNativeCapacitorPlatform()) {
    throw new IapError('unavailable', 'not_native');
  }
  if (typeof window === 'undefined') {
    throw new IapError('unavailable', 'no_window');
  }
  if (!initialized) {
    // On-Demand-Reinit (7.1.0.65): Die Start-Init kann bei langsamem Cordova-Boot
    // noch nicht durch sein ODER einmalig fehlgeschlagen sein. Beim Kauf-Tap einen
    // frischen Versuch anstoßen (Retry-Zähler zurücksetzen), statt sofort
    // aufzugeben. Bleibt es dabei, mit der EXAKTEN Ursache werfen.
    iapInitAttempts = 0;
    await initIap(lastProducts);
    if (!initialized) throw new IapError('unavailable', 'iap_not_initialized');
  }

  const cdvPlatform = getCapacitorPlatform();
  if (!cdvPlatform) throw new IapError('unavailable', 'unknown_platform');

  const cdv = getCdv();
  if (!cdv?.store) throw new IapError('unavailable', 'store_missing');
  const { store, Platform } = cdv;
  const platformEnum: CdvPurchase.Platform = cdvPlatform === 'ios'
    ? Platform.APPLE_APPSTORE
    : (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform);

  // Offer kann nach store.initialize() noch laden — kurz darauf warten, statt
  // sofort 'offer_not_loaded' zu werfen (haertet den „nicht verfuegbar"-Fehler).
  let offer = await waitForOffer(store, productId, platformEnum);
  // Fresh-Install-Bug: Wenn das Produkt GAR NICHT im Store registriert ist, kommt
  // waitForOffer nie zum Erfolg (unregistrierte Produkte bekommen keine Offers
  // nachgeladen). EIN Nachversuch: Coin-Pakete frisch vom Server holen (Boot-
  // 401 ist jetzt hinfällig, da der User eingeloggt ist), pending filtern und
  // beim Store nachregistrieren, dann nochmal kurz auf das Offer warten.
  // Scheitert auch das: wie bisher IapError('unavailable', 'offer_not_loaded').
  // KEINE Endlosschleife — genau ein Nachversuch. Fetch-Fehler (401, Netzwerk)
  // werden toleriert und fuehren direkt zum 'offer_not_loaded'-Fallback.
  if (!offer && !store.get(productId, platformEnum)) {
    try {
      const response = await getCoinIapPackages();
      const freshPacks = response.packs
        .filter((p) => !(p as { pending?: boolean }).pending)
        .map((p) => ({
          productId: p.id,
          coins: p.coins,
          priceEur: p.priceEur,
          name: p.name,
        }));
      await ensureProductsRegistered(freshPacks);
      offer = await waitForOffer(store, productId, platformEnum);
    } catch (err) {
      console.warn('[iap] purchaseCoins: on-demand Re-Fetch/Nachregistrierung fehlgeschlagen:', err);
    }
  }
  if (!offer) throw new IapError('unavailable', 'offer_not_loaded');

  // FIX 03-P2-2 + Doppel-Tap-Guard: NUR EIN Kauf gleichzeitig (pending.size > 0).
  // Vorher blockte der Guard nur denselben productId → zwei schnelle Käufe
  // UNTERSCHIEDLICHER Produkte konnten beide pending sein, und der store-error-/
  // approved-Callback (feuert oft OHNE productId) rejectete dann per „letzter
  // Eintrag"-Heuristik den FALSCHEN Kauf → verlorene Coins. Mit dieser Serialisierung
  // gibt es nie mehr als einen pending-Eintrag → die Zuordnung ist immer eindeutig.
  if (pending.size > 0 || preparingSubscription || restoringSubscriptions || unresolvedAttempts.size > 0) {
    throw new IapError('store_error', 'purchase_already_in_progress');
  }

  // Erst die pending-Promise anlegen, DANN store.order rufen — sonst kann
  // ein sehr schneller approved-Callback in ein "kein pending" rennen.
  const purchasePromise = new Promise<IapPurchaseResult>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      const entry = pending.get(productId);
      if (entry) {
        pending.delete(productId);
        entry.reject(new IapError('store_error', 'purchase_timeout'));
      }
    }, PURCHASE_TIMEOUT_MS);
    pending.set(productId, { resolve, reject, timeoutId });
  });

  const entry = pending.get(productId)!;
  entry.attempt = createPurchaseAttempt({ product_id: productId });
  entry.platform = cdvPlatform;
  setPurchasePhase(entry.attempt, 'order');
  void Promise.resolve().then(() => store.order(offer!)).then(error => {
    if (error) return settleOrderFailure(productId, entry, error);
  }, error => settleOrderFailure(productId, entry, error));

  return purchasePromise;
}

/**
 * Loest einen Abo-Kauf (Flatrate) aus. tier+duration → Apple-productId bzw.
 * Google-productId + base plan. Resolved nach serverseitiger Verifizierung
 * (verify-receipt / verify-purchase legt das Abo in flat_subscriptions an);
 * wirft IapError bei Abbruch/Fehler. Auf Web wirft es IapError('unavailable').
 */
export async function purchaseSubscription(
  tier: SubscriptionTier,
  duration: SubscriptionDuration,
  config: SubscriptionPurchaseConfig = {},
): Promise<IapPurchaseResult> {
  const attempt = config.attempt ?? createPurchaseAttempt();
  config.attempt = attempt;
  if (pending.size > 0 || preparingSubscription || restoringSubscriptions || unresolvedAttempts.size > 0) throw new IapError('store_error', 'purchase_already_in_progress');
  preparingSubscription = true;
  try {
    if (purchasesBlockedByEnvironment()) throw new IapError('unavailable', 'beta_environment');
    if (!await isNativeCapacitorPlatform()) throw new IapError('unavailable', 'not_native');
    if (typeof window === 'undefined') throw new IapError('unavailable', 'no_window');
    if (!initialized) {
      // On-Demand-Reinit (7.1.0.65) analog zu purchaseCoins.
      iapInitAttempts = 0;
      await initIap(lastProducts);
      if (!initialized) throw new IapError('unavailable', 'iap_not_initialized');
    }

    const cdvPlatform = getCapacitorPlatform();
    if (!cdvPlatform) throw new IapError('unavailable', 'unknown_platform');

    const sub = findSubByTierDuration(tier, duration);
    if (!sub) throw new IapError('unavailable', 'unknown_subscription');

    const cdv = getCdv();
    if (!cdv?.store) throw new IapError('unavailable', 'store_missing');
    const { store, Platform } = cdv;
    const isAndroid = cdvPlatform === 'android';
    const platformEnum: CdvPurchase.Platform = isAndroid
      ? (ANDROID_CDV_PLATFORM as unknown as CdvPurchase.Platform)
      : Platform.APPLE_APPSTORE;

    // Store-productId: Apple = tier-spezifische id, Google = die eine flat-id.
    const storeProductId = isAndroid
      ? config.googleProductId ?? sub.googleProductId ?? GOOGLE_SUB_PRODUCT_ID
      : config.appleProductId ?? sub.appleProductId;
    const googleBasePlanId = config.googleBasePlanId ?? sub.googleBasePlanId;

    Object.assign(attempt.meta, { product_id: storeProductId, base_plan_id: isAndroid ? googleBasePlanId : undefined,
      offer_kind: config.resolvedOffer?.kind === 'trial' ? 'trial' : config.resolvedOffer?.kind === 'discount' ? 'intro' : 'base',
      old_token_present: false });
    const native = await nativeRequestInfo();
    Object.assign(attempt.meta, { native_version: native['x-lorath-wrapper-ver'], native_build: native['x-lorath-native-build'] });
    if (Date.now() - detailsLoadedAt > 30 * 60_000) {
      await store.update();
      detailsLoadedAt = Date.now();
    }
    let offer: CdvPurchase.Offer | undefined;
    if (config.resolvedOffer) {
      const binding = resolvedOfferBindings.get(config.resolvedOffer);
      const available = store.get(storeProductId, platformEnum)?.offers ?? [];
      const refreshed = available.find(candidate => candidate.id === binding?.offer.id
        && JSON.stringify(candidate.pricingPhases) === binding?.phases);
      if (!binding || binding.productId !== storeProductId || binding.tier !== tier || binding.duration !== duration
        || !refreshed || JSON.stringify(binding.offer.pricingPhases) !== binding.phases) {
        throw new IapError('unavailable', 'offer_missing');
      }
      offer = refreshed;
    } else {
      offer = await waitForSubscriptionOffer(
        store, storeProductId, platformEnum, googleBasePlanId, isAndroid, config.googleOfferId,
      );
    }
    if (!offer) throw new IapError('unavailable', 'offer_not_loaded');
    if (isAndroid && !(offer as CdvPurchase.GooglePlay.SubscriptionOffer).token) throw new IapError('unavailable', 'offer_missing');

    // Apple verlangt die Signatur genau für das gebundene Promotional Offer.
    // Fehlende/abweichende Daten dürfen niemals zum regulären Kauf zurückfallen.
    if (!isAndroid && config.resolvedOffer?.kind === 'discount'
        && (!config.appleDiscount?.signature || config.appleDiscount.id !== offer.id)) {
      throw new IapError('unavailable', 'offer_missing');
    }
    let additionalData: CdvPurchase.AdditionalData | undefined = !isAndroid && config.appleDiscount
      ? { appStore: { discount: config.appleDiscount } } : undefined;
    if (isAndroid && config.replace) {
      let tokens = readGoogleSubscriptionTokens().slice(0, 10);
      if (!tokens.length) {
        const error = await store.restorePurchases();
        if (error) throw fromStoreError(error, 'prepare');
        for (let retry = 0; retry < 10 && !tokens.length; retry += 1) {
          if (retry) await new Promise<void>(resolve => window.setTimeout(resolve, 1000));
          tokens = readGoogleSubscriptionTokens().slice(0, 10);
        }
      }
      if (!tokens.length) throw new IapError('unavailable', 'replace_token_missing');
      const candidate = await getGoogleReplaceCandidate(tokens);
      if (!candidate.ok || candidate.index === null || !Number.isInteger(candidate.index) || !tokens[candidate.index]
        || (config.replace.contractRef && candidate.contract_ref !== config.replace.contractRef)) {
        throw new IapError('unavailable', 'replace_token_missing');
      }
      const oldPurchaseToken = tokens[candidate.index];
      const localOldProductId = (store.localReceipts ?? []).flatMap(receipt => receipt.transactions ?? [])
        .find(transaction => readGooglePurchaseToken(transaction) === oldPurchaseToken)?.products.find(product => GOOGLE_SUB_IDS.has(product.id))?.id;
      const oldProductId = config.replace.oldProductId ?? localOldProductId;
      // Google erlaubt DEFERRED hier nicht. Kürzere Basispläne erst nach Ablauf neu kaufen.
      const durationRank: Record<string, number> = { week: 1, month: 2, year: 3 };
      if (oldProductId === storeProductId && (config.replace.mode === 'downgrade'
          || (durationRank[config.replace.oldBasePlanId ?? ''] ?? 0) > (durationRank[googleBasePlanId ?? ''] ?? 0))) {
        throw new IapError('unavailable', 'plan_change_at_period_end', { phase: 'prepare' });
      }
      const mode = oldProductId === storeProductId ? 'WITHOUT_PRORATION'
        : config.replace.mode === 'upgrade' ? 'WITH_TIME_PRORATION' : 'DEFERRED';
      const replacementMode = cdv.GooglePlay.ReplacementMode[mode];
      if (!replacementMode) throw new IapError('unavailable', 'replacement_mode_unavailable');
      Object.assign(attempt.meta, { replacement_mode: mode, old_token_present: true });
      additionalData = { googlePlay: { oldPurchaseToken, replacementMode,
        ...(config.googleAccountId && config.replace.contractHasAccountId ? { accountId: config.googleAccountId } : {}) } };
    } else if (isAndroid && config.googleAccountId) {
      additionalData = { googlePlay: { accountId: config.googleAccountId } };
    }
    preparingSubscription = false;
    googleSubBasePlanInFlight = isAndroid ? googleBasePlanId : null;
    const purchasePromise = new Promise<IapPurchaseResult>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        const entry = pending.get(storeProductId);
        if (entry) {
          pending.delete(storeProductId);
          googleSubBasePlanInFlight = null;
          entry.reject(new IapError('store_error', 'purchase_timeout'));
        }
      }, PURCHASE_TIMEOUT_MS);
      pending.set(storeProductId, { resolve, reject, timeoutId, attempt, platform: cdvPlatform,
        existingTransactions: localTransactionKeys(), funnel: config.funnel });
    });

    const entry = pending.get(storeProductId)!;
    setPurchasePhase(attempt, 'order');
    if (config.funnel) trackFunnel('checkout_started', { ...config.funnel, meta: purchaseDiagnostics(attempt) });
    void Promise.resolve().then(() => store.order(offer!, additionalData)).then(error => {
      if (error) return settleOrderFailure(storeProductId, entry, error);
    }, error => settleOrderFailure(storeProductId, entry, error));
    return await purchasePromise;
  } catch (error) {
    if (error instanceof IapError) { error.phase ??= attempt.phase; throw error; }
    if (error instanceof ApiError) {
      const failure = fromVerificationError(error);
      failure.phase = attempt.phase;
      throw failure;
    }
    throw fromStoreError(error, attempt.phase);
  } finally { preparingSubscription = false; }
}

async function finishRestoredTransactions(transactions: CdvPurchase.Transaction[]): Promise<void> {
  for (const tx of transactions) {
    const key = transactionKey(tx);
    if (completedTransactions.has(key)) continue;
    try { await tx.finish(); completedTransactions.add(key); }
    catch { /* Die Store-Warteschlange bleibt für einen späteren Abschluss erhalten. */ }
  }
}

const pendingRestores = new Map<string, { timer?: number; retries: number; run: () => Promise<void> }>();
function scheduleRestoreVerification(attempt: PurchaseAttempt, client?: Pick<QueryClient, 'invalidateQueries'>, transactions: CdvPurchase.Transaction[] = []): void {
  const job = pendingRestores.get(attempt.id) ?? { retries: 0, run: async () => {} };
  job.run = async () => {
    try {
      const response = await getVerificationStatus(attempt.id);
      if (response.status === 'verified') {
        pendingRestores.delete(attempt.id);
        await finishRestoredTransactions(transactions);
        updatePurchaseStatus('verified', attempt.id);
        const queryClient = client ?? await getAppQueryClient();
        await queryClient.invalidateQueries({ queryKey: ['membership'] });
        return;
      }
      if (response.status === 'failed') {
        pendingRestores.delete(attempt.id);
        updatePurchaseStatus(response.error_code === 'subscription_owner_mismatch' ? 'owner_conflict' : 'idle', attempt.id);
        return;
      }
    } catch { /* Netzfehler sind kein Nachweis für fehlende Käufe. */ }
    if (job.retries < 5) {
      job.retries += 1;
      job.timer = window.setTimeout(() => { job.timer = undefined; void job.run(); }, 60_000);
    }
  };
  pendingRestores.set(attempt.id, job);
  job.timer = window.setTimeout(() => { job.timer = undefined; void job.run(); }, 60_000);
}

/** Wiederherstellung wartet auf den Server oder zeigt eine ausstehende Bestätigung. */
export async function restoreSubscriptions(queryClientArg?: Pick<QueryClient, 'invalidateQueries'>): Promise<{ status: 'pending' | 'verified' }> {
  if (purchasesBlockedByEnvironment()) throw new IapError('unavailable', 'beta_environment');
  if (!await isNativeCapacitorPlatform()) throw new IapError('unavailable', 'not_native');
  if (!initialized) await initIap(lastProducts);
  if (!initialized) throw new IapError('unavailable', 'iap_not_initialized');
  const store = getCdv()?.store;
  if (!store) throw new IapError('unavailable', 'store_missing');

  if (pending.size || preparingSubscription || restoringSubscriptions) throw new IapError('store_error', 'purchase_already_in_progress');
  restoringSubscriptions = true;
  try {
    const error = await store.restorePurchases();
    if (error) throw fromStoreError(error);
    let found = false;
    let confirmed = false;
    let failed = false;
    for (let attempt = 0; attempt < 10; attempt += 1) {
      if (attempt) await new Promise<void>((resolve) => window.setTimeout(resolve, 1000));
      const tokens = activePlatform === 'android' ? readGoogleSubscriptionTokens() : [];
      const receipt = activePlatform === 'ios' ? readAppleAppStoreReceipt() : null;
      if (!tokens.length && !receipt) continue;
      found = true;
      const transactions = (store.localReceipts ?? []).flatMap(item => item.transactions ?? [])
        .filter(tx => !tx.isPending && tx.products?.some(product => isSubscriptionProductId(product.id)));
      const results = await Promise.allSettled(receipt
        ? [refreshAppleSubs(receipt, { attempt_id: createAttemptId(), recovery: true })]
        : tokens.map((token) => refreshGoogleSubs(token, { attempt_id: createAttemptId(), recovery: true })));
      for (const [index, result] of results.entries()) {
        if (result.status === 'rejected') {
          const failure = fromVerificationError(result.reason);
          if (classifyPurchaseFailure(failure) === 'owner_conflict') throw failure;
          failed = true; continue;
        }
        const verifiedTransactions = transactions.filter(tx => receipt || readGooglePurchaseToken(tx) === tokens[index]);
        let response = result.value as (VerificationResponse & { updated?: unknown[] }) | null;
        if (response?.status === 'pending') {
          const attempt = createPurchaseAttempt();
          if (response.attempt_id) {
            attempt.id = response.attempt_id;
            response = await pollVerification(attempt);
          } else updatePurchaseStatus('delayed', attempt.id);
          if (response?.status === 'pending') {
            if (response.attempt_id) attempt.id = response.attempt_id;
            scheduleRestoreVerification(attempt, queryClientArg, verifiedTransactions);
            return { status: 'pending' };
          }
          if (response?.status === 'failed') throw fromVerificationError(new ApiError(200, response));
          updatePurchaseStatus('idle');
        }
        if (response && response.ok !== false && (response.ok === true || Array.isArray(response.updated))) {
          confirmed = true;
          await finishRestoredTransactions(verifiedTransactions);
        }
        else failed = true;
      }
      break;
    }
    if (!found) throw new IapError('verify_failed', 'iap.restore.nothing_found');
    if (!confirmed) throw new IapError('verify_failed', failed ? 'restore_failed' : 'iap.restore.nothing_found');
    const snapshot = await getMembership();
    if (!['plus', 'ultra', 'founders', 'torchbearer'].includes(snapshot.entitlements.tier)) {
      throw new IapError('verify_failed', 'iap.restore.nothing_found');
    }
    updatePurchaseStatus('idle');
    const queryClient = queryClientArg ?? await getAppQueryClient();
    await queryClient.invalidateQueries({ queryKey: ['membership'], exact: true });
    await queryClient.invalidateQueries({ queryKey: ['membership', 'snapshot'] });
    await queryClient.invalidateQueries({ queryKey: ['membership', 'overview'] });
    return { status: 'verified' };
  } catch (error) {
    const failure = error instanceof IapError ? error : fromVerificationError(error);
    updatePurchaseStatus(classifyPurchaseFailure(failure) === 'owner_conflict' ? 'owner_conflict' : 'idle');
    throw failure;
  } finally { restoringSubscriptions = false; }
}

/**
 * Liest die aktuelle Coin-Balance. Wird nach erfolgreichem Kauf ggf. neu
 * geladen; hier als re-export, damit Aufrufer nicht zwei Module importieren
 * muessen.
 */
export type { CoinBalance };
